import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { StatusBadge } from "@/components/StatusBadge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { api, formatApiError, formatINR } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ArrowRight, IndianRupee, Package, Weight, Calendar, Search, Gavel, ShieldAlert } from "lucide-react";

export default function OwnerDashboard() {
  const { user } = useAuth();
  const [loads, setLoads] = useState([]);
  const [myBidLoads, setMyBidLoads] = useState([]);
  const [stats, setStats] = useState({});
  const [q, setQ] = useState("");

  const load = async () => {
    try {
      const [{ data: a }, { data: mine }, { data: s }] = await Promise.all([
        api.get("/loads?scope=all"),
        api.get("/loads?scope=mine"),
        api.get("/stats"),
      ]);
      setLoads(a.loads);
      setMyBidLoads(mine.loads);
      setStats(s);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return loads;
    return loads.filter(l =>
      [l.title, l.source_city, l.destination_city, l.material, l.vehicle_type].join(" ").toLowerCase().includes(s)
    );
  }, [loads, q]);

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 py-10">
        <div className="overline text-[#FFD700]">LORRY YARD</div>
        <h1 className="font-display font-black text-4xl mt-2 tracking-tight" data-testid="owner-dashboard">Bid. Book. Roll.</h1>

        {user && !user.verified && (
          <div data-testid="owner-verification-banner" className="mt-6 p-4 border border-[#FFD700]/40 bg-[#FFD700]/10 text-[#FFD700] flex items-start gap-3 rounded-sm">
            <ShieldAlert className="w-5 h-5 mt-0.5" />
            <div className="text-sm flex-1">
              <div className="font-semibold">Bidding locked — verify your documents</div>
              <div className="text-xs opacity-80 mt-1">Upload your Vehicle RC, Insurance and Driving License. Admin usually approves within a few hours.</div>
            </div>
            <Link to="/documents">
              <button data-testid="owner-goto-docs" className="text-xs uppercase tracking-wider font-bold px-3 py-2 bg-[#FFD700] text-black rounded-sm">Upload docs</button>
            </Link>
          </div>
        )}

        <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 gap-3">
          <Stat label="Available loads" value={stats.available_loads ?? 0} accent />
          <Stat label="My bids" value={stats.my_bids ?? 0} />
          <Stat label="Won" value={stats.won ?? 0} />
        </div>

        <Tabs defaultValue="market" className="mt-10">
          <TabsList className="bg-[#141414] border border-[#2A2A2A] rounded-sm p-1">
            <TabsTrigger value="market" data-testid="tab-market" className="rounded-sm data-[state=active]:bg-[#FFD700] data-[state=active]:text-black">Load Market</TabsTrigger>
            <TabsTrigger value="mybids" data-testid="tab-mybids" className="rounded-sm data-[state=active]:bg-[#FFD700] data-[state=active]:text-black">My Bids</TabsTrigger>
          </TabsList>

          <TabsContent value="market" className="mt-6">
            <div className="relative max-w-md mb-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <Input value={q} onChange={(e) => setQ(e.target.value)}
                data-testid="search-loads-input"
                placeholder="Search by route, material, vehicle..."
                className="h-11 pl-10 bg-[#141414] border-[#2A2A2A] focus-visible:ring-[#FFD700] rounded-sm" />
            </div>
            <LoadGrid loads={filtered} empty="No open loads right now — check back soon." />
          </TabsContent>

          <TabsContent value="mybids" className="mt-6">
            <LoadGrid loads={myBidLoads} empty="You haven't bid on any load yet. Head to Load Market." />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className={`p-5 border ${accent ? "border-[#FFD700] bg-[#FFD700]/10" : "border-[#2A2A2A] bg-[#141414]"}`}>
      <div className="overline text-zinc-400">{label}</div>
      <div className={`font-display font-black text-3xl mt-2 ${accent ? "text-[#FFD700]" : ""}`}>{value}</div>
    </div>
  );
}

function LoadGrid({ loads, empty }) {
  if (!loads.length) return <div className="p-12 border border-dashed border-[#2A2A2A] text-center text-zinc-500">{empty}</div>;
  return (
    <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
      {loads.map((l) => (
        <Link to={`/loads/${l.id}`} key={l.id} data-testid={`market-load-${l.id}`}
          className="group p-5 bg-[#141414] border border-[#2A2A2A] hover:border-[#FFD700]/60 hover:-translate-y-1 transition-transform block">
          <div className="flex items-start justify-between gap-2">
            <div className="font-display font-bold text-lg leading-tight">{l.title}</div>
            <StatusBadge status={l.status} />
          </div>
          <div className="mt-4 flex items-center gap-2 font-mono-ll text-sm">
            <span>{l.source_city}</span>
            <ArrowRight className="w-4 h-4 text-[#FFD700]" />
            <span>{l.destination_city}</span>
          </div>
          <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
            <Meta icon={Package} label={l.material} />
            <Meta icon={Weight} label={`${l.weight_kg} kg`} />
            <Meta icon={Calendar} label={new Date(l.pickup_date).toLocaleDateString("en-IN")} />
            <Meta icon={IndianRupee} label={`Budget ${formatINR(l.budget_inr)}`} />
          </div>
          <div className="mt-4 pt-4 border-t border-[#2A2A2A] flex items-center justify-between">
            <span className="text-sm text-zinc-400">{l.vehicle_type}</span>
            <span className="inline-flex items-center gap-1 text-[#FFD700] text-sm font-semibold">
              <Gavel className="w-4 h-4" /> Place bid
            </span>
          </div>
        </Link>
      ))}
    </div>
  );
}

function Meta({ icon: I, label }) {
  return (
    <div className="flex items-center gap-2 text-zinc-400">
      <I className="w-4 h-4 text-[#FFD700]" />
      <span className="truncate">{label}</span>
    </div>
  );
}
